import { DefaultTheme } from 'styled-components/macro';

type BreakpointsKeysT = { [key in keyof typeof breakpointsNumInPx]: string };

export const breakpointsNumInPx = {
  xs: 320,
  sm: 480,
  md: 768,
  lg: 992,
  xl: 1280,
  '2xl': 1440,
};
const breakpointsInEm: BreakpointsKeysT = {
  xs: '20em',
  sm: '30em',
  md: '48em',
  lg: '62em',
  xl: '80em',
  '2xl': '90em',
};
export const QUERY_TYPE = '@media screen';
export const ORIENTATION = {
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape',
};
export const devices = {
  smallMobile: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.xs})`,
  mobile: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.sm})`,
  tablet: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.md})`,
  tabletLandscape: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.md}) and (orientation: ${ORIENTATION.LANDSCAPE})`,
  smallDesktop: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.lg})`,
  desktop: `${QUERY_TYPE} and (min-width: ${breakpointsInEm.xl})`,
  largeDesktop: `${QUERY_TYPE} and (min-width: ${breakpointsInEm['2xl']})`,
  iOS: `${QUERY_TYPE} and (-webkit-min-device-pixel-ratio: 2)`,
};

const mainTheme: DefaultTheme = {
  colors: {
    white: '#FFFFFF',
    black: '#000000',
    transparent: 'transparent',
    primary: {
      variant1: '#182955',
      variant2: '#F8F9FA',
      variant3: '#E9ECEF',
      variant4: '#E5E5E5',
      variant5: '#DEE2E6',
      variant6: '#CED4DA',
      variant7: '#ADB5BD',
      variant8: '#6C757D',
      variant9: '#495057',
      variant10: '#343A40',
      variant11: '#212529',
      variant12: '#157515',
      variant13: '#815f94',
      variant14: '#f9f1fa',
      variant15: '#014361',
      variant16: '#4271EB',
    },
    secondary: {
      variant1: '#5CD477',
      variant2: '#72CF76',
      variant3: '#E8EEFF',
      variant4: '#3F6BDE',
      variant5: '#3459B8',
      variant6: '#7ce896',
      variant7: '#673AB7',
    },
    highlight: {
      variant1: '#FB9702',
      variant2: '#DF6E6E',
      variant3: '#B54FBA',
      variant4: '#E7DA0B',
      variant5: '#DE350B',
      variant6: '#E5F6FD',
    },
    rgba: {
      variant1: 'rgba(0, 0, 0, 0.45)',
      variant2: 'rgba(255, 255, 255, 0.2)',
      variant3: 'rgba(0, 0, 0, 0.7)',
      variant4: 'rgba(0, 0, 0, 0.87)',
      variant5: 'rgba(0, 0, 0, 0.23)',
    },
  },
  fontFamily: {
    primary: '"Roboto", sans-serif',
    secondary: '"Barlow", sans-serif',
  },
  fontSize: {
    xxs: '0.688rem', // 11px
    xs: '0.75rem', // 12px
    sm: '0.813rem', // 13px
    md: '0.875rem', // 14px
    lg: '0.938rem', // 15px
    xl: '1rem', // 16px
    '2xl': '1.125rem', // 18px
    '3xl': '1.313rem', // 21px
    '4xl': '1.5rem', // 24px
    '5xl': '1.875rem', // 30px
  },
  fontWeight: {
    hairline: 100,
    ultraLight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extraBold: 800,
    black: 900,
  },
  shadows: {
    variant1: '0px 4px 9px rgba(0, 0, 0, 0.08)',
    variant2: '0px 0px 6px 4px rgb(0 0 0 / 0.1)',
  },
  radius: {
    xs: '0.125rem', // 2px
    sm: '0.25rem', // 4px
    md: '0.375rem', // 6px
    lg: '0.5rem', // 8px
    xl: '0.625rem', // 10px
    '2xl': '4rem', // 64px
    half: '50%',
  },
  borders: {
    none: 'none',
    xs: '1px solid',
    sm: '2px solid',
    md: '3px solid',
    lg: '4px solid',
    xl: '5px solid',
    '2xl': '6px solid',
    '3xl': '8px solid',
    '4xl': '10px solid',
    '5xl': '12px solid',
  },
  filters: {
    whiteToViolet:
      'invert(34%) sepia(95%) saturate(2134%) hue-rotate(214deg) brightness(97%) contrast(89%)',
  },
  sidebarOptions: {
    openedWidth: '370px',
    collapsedWidth: '50px',
  },
  breakpointsNumInPx,
  devices,
};

export default mainTheme;
